<template>
    <section class="scan">
        <div class="scan__camera">
            <div class="scan__video-wrap">
                <video ref="videoEl" class="scan__video" autoplay muted playsinline />

                <ScanOverlay
                    ref="scanOverlay"
                    :shape="scanShape"
                    :overlay-width="overlayW"
                    :overlay-height="overlayH"
                    :hole="hole"
                    :mask-id="maskId"
                    :reticle-style="reticleStyle"
                    :show-hint="shouldShowHint"
                    :show-preview="Boolean(previewImageUrl && isLoadingMock)"
                    :preview-image-url="previewImageUrl"
                    hint-text="Point at the label and tap to scan"
                />

                <div class="scan__top-bar">
                    <button
                        class="help scan__top-btn"
                        type="button"
                        aria-label="Show tip"
                        @click="showHelp = true"
                    >
                        <img src="@/assets/icons/question.svg" alt="Show tip" />
                    </button>

                    <div class="scan__shape-switcher">
                        <button
                            class="shape-btn"
                            :class="{ 'shape-btn_active': scanShape === 'rectangle' }"
                            type="button"
                            aria-label="Rectangle scan area"
                            @click="scanShape = 'rectangle'"
                        >
                            <svg
                                width="28"
                                height="18"
                                viewBox="0 0 28 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <rect
                                    x="1.5"
                                    y="1.5"
                                    width="25"
                                    height="15"
                                    rx="3"
                                    stroke="currentColor"
                                    stroke-width="2"
                                />
                            </svg>
                        </button>
                        <button
                            class="shape-btn"
                            :class="{ 'shape-btn_active': scanShape === 'circle' }"
                            type="button"
                            aria-label="Circle scan area"
                            @click="scanShape = 'circle'"
                        >
                            <svg
                                width="22"
                                height="22"
                                viewBox="0 0 22 22"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <circle
                                    cx="11"
                                    cy="11"
                                    r="9.5"
                                    stroke="currentColor"
                                    stroke-width="2"
                                />
                            </svg>
                        </button>
                        <button
                            class="shape-btn"
                            :class="{ 'shape-btn_active': scanShape === 'square' }"
                            type="button"
                            aria-label="Square scan area"
                            @click="scanShape = 'square'"
                        >
                            <svg
                                width="22"
                                height="22"
                                viewBox="0 0 22 22"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <rect
                                    x="1.5"
                                    y="1.5"
                                    width="19"
                                    height="19"
                                    rx="3"
                                    stroke="currentColor"
                                    stroke-width="2"
                                />
                            </svg>
                        </button>
                    </div>

                    <div class="scan__top-right">
                        <div
                            v-if="flashSupported"
                            class="flash-btn"
                            :class="{ 'flash-btn_active': isFlashOn }"
                            role="button"
                            @click="handleToggleFlash"
                            :aria-label="isFlashOn ? 'Turn off the flash' : 'Turn on the flash'"
                        >
                            <FlashIcon :isActive="isFlashOn" />
                        </div>
                        <div v-else class="scan__top-placeholder" />
                    </div>
                </div>

                <div class="scan__shutter-wrap">
                    <button
                        class="shutter"
                        type="button"
                        :disabled="!isActive || isInitializing"
                        @click="handleShutter"
                        aria-label="Take a photo"
                    >
                        <span class="shutter__inner" />
                    </button>
                </div>

                <!-- <div v-if="zoomSupported" class="scan__zoom-controls">
                    <div
                        v-for="level in zoomLevels"
                        :key="level"
                        class="zoom-btn"
                        :class="{ 'zoom-btn_active': zoomLevel === level }"
                        @click="setZoomLevel(level)"
                    >
                        {{ level }}
                    </div>
                </div> -->

                <div v-if="isInitializing" class="scan__initializing">
                    <div class="loader">
                        <img src="@/assets/icons/loader_scan.svg" class="loader__spinner" />
                        <div class="loader__title">Launching the camera...</div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="error" class="scan__error">
            {{ error }}
        </div>

        <div v-if="showHelp" class="scan__help-modal">
            <div class="scan__help-modal-bg" />
            <div class="scan__help-content">
                <div
                    class="scan__help-close help"
                    type="button"
                    aria-label="Close the tip"
                    @click="showHelp = false"
                >
                    <img src="@/assets/icons/close.svg" alt="Close the tip" />
                </div>
                <div class="scan__help-title">Tip</div>
                <p class="scan__help-desc">
                    For correct scanning, move the camera closer so that the scanning area matches
                    the shape of the label. Make sure the entire label is in focus.
                </p>
            </div>
        </div>

        <div v-if="isLoadingMock" class="scan__loader-modal">
            <div class="loader">
                <img src="@/assets/icons/loader_scan.svg" class="loader__spinner" />
                <div class="loader__title">Recognizing label...</div>
            </div>
        </div>

        <Transition name="error-modal">
            <div v-if="mockError" class="scan__error-modal" ref="errorModalEl">
                <div class="scan__error-modal-bg" @click="closeError" />
                <div
                    class="error"
                    @click.stop
                    ref="errorContentEl"
                    :style="{
                        transform: `translateY(${errorDragY}px)`,
                        opacity:
                            isDragging || isErrorModalClosing
                                ? Math.max(0, 1 - errorDragY / 300)
                                : 1,
                        transition: isDragging
                            ? 'none'
                            : 'transform 0.3s ease-out, opacity 0.3s ease-out',
                    }"
                >
                    <div class="error__swipe" ref="errorSwipeEl"></div>
                    <div class="error__text-wrap">
                        <div class="error__title">{{ errorModalTitle }}</div>
                        <div class="error__desc">
                            {{ errorModalDescription }}
                        </div>
                    </div>
                    <img class="error__img" src="@/assets/images/scan_error.png" alt="Scan error" />
                    <div class="error__actions">
                        <button class="btn" type="button" @click="closeError">
                            {{ errorModalActionText }}
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </section>
</template>

<script setup lang="ts">
import FlashIcon from '@/components/icons/flashIcon.vue'
import ScanOverlay from '@/components/ScanOverlay.vue'
import { useCamera } from '@/composables/useCamera'
import { useCameraDebug } from '@/composables/useCameraDebug'
import { useCameraOptimizer } from '@/composables/useCameraOptimizer'
import { useCameraSelector } from '@/composables/useCameraSelector'
import type { CameraScore } from '@/composables/useCameraSelector'
import { useImageProcessor } from '@/composables/useImageProcessor'
import { useViewport } from '@/composables/useViewport'
import { apiService } from '@/services/api'
import { useUiStore } from '@/stores/ui'
import { useRegisterStore } from '@/stores/register'
import type { ScanShape } from '@/types'
import { calculateScanHole } from '@/utils/scanGeometry'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const uiStore = useUiStore()
useViewport()

type ScanOverlayRef = {
    overlayEl: HTMLDivElement | null
    reticleEl: HTMLDivElement | null
}

const videoEl = ref<HTMLVideoElement | null>(null)
const scanOverlay = ref<ScanOverlayRef | null>(null)
const reticleEl = computed(() => scanOverlay.value?.reticleEl ?? null)
const overlayEl = computed(() => scanOverlay.value?.overlayEl ?? null)
const errorContentEl = ref<HTMLDivElement | null>(null)

const overlayW = ref(0)
const overlayH = ref(0)
const maskId = `register-mask-${Math.random().toString(36).slice(2)}`
const hole = ref({ x: 0, y: 0, w: 0, h: 0 })
const reticleStyle = computed(() => ({
    width: `${hole.value.w}px`,
    height: `${hole.value.h}px`,
}))

const {
    isActive,
    stream,
    error,
    startCameraWithRetry,
    getCameraDevices,
    stopCamera,
    getStoredPreferredCameraLabel,
    setStoredPreferredCameraLabel,
} = useCamera()

const { selectBestCameraByScore } = useCameraSelector()

const {
    getConstraintsLevels,
    optimizeVideoTrack,
    // zoomSupported,
    flashSupported,
    // setZoom,
    toggleFlash,
} = useCameraOptimizer()

const { updateCamerasList, updateSelectedCamera, updateDeviceInfo, addError } = useCameraDebug()

const { captureAndCrop, revokeObjectURL } = useImageProcessor()

const abortController = ref<AbortController | null>(null)

const showHelp = ref(false)
const isLoadingMock = ref(false)
const mockError = ref<string | null>(null)
const mockErrorKind = ref<'scan' | 'already-attached'>('scan')
const attachedProductId = ref<string | null>(null)
const attachedProductName = ref<string | null>(null)
const isErrorModalClosing = ref(false)
const previewImageUrl = ref<string | null>(null)
const isInitializing = ref(false)

const errorDragY = ref(0)
const isDragging = ref(false)

// const zoomLevel = ref(1)
// const zoomLevels = [1, 3, 5]
const isFlashOn = ref(false)

const scanShape = ref<ScanShape>('rectangle')

const errorModalTitle = computed(() =>
    mockErrorKind.value === 'already-attached' ? 'Label already attached' : 'Retry scan'
)
const errorModalDescription = computed(() => {
    if (mockErrorKind.value === 'already-attached') {
        const productLabel = attachedProductName.value || attachedProductId.value

        return productLabel
            ? `This label is already registered and attached to product ${productLabel}. Please retry with another one.`
            : 'This label is already registered in our database. Please retry with another one.'
    }

    return 'Make sure the edges of the label are within the frame and the dots are in focus.'
})
const errorModalActionText = computed(() =>
    mockErrorKind.value === 'already-attached' ? 'Scan to register' : 'Back to scan'
)

const isMobile = computed(() => {
    const ua = navigator.userAgent || ''
    const isTouch = matchMedia('(pointer: coarse)').matches
    return /Mobile|Android|iP(hone|od|ad)|IEMobile|BlackBerry/i.test(ua) || isTouch
})

const isSecure = computed(() => {
    return (
        (window as unknown as { isSecureContext?: boolean }).isSecureContext === true ||
        location.protocol === 'https:' ||
        location.hostname === 'localhost' ||
        location.hostname === '127.0.0.1'
    )
})

const shouldShowHint = computed(() => {
    return !showHelp.value && !uiStore.anyLoading && !isInitializing.value
})

const attachStreamToVideo = () => {
    if (videoEl.value && stream.value) {
        videoEl.value.srcObject = stream.value
        const playPromise = videoEl.value.play()
        if (playPromise && typeof playPromise.then === 'function') {
            playPromise.catch(() => {})
        }
    }
}

const isGoodCameraByCaps = (caps: MediaTrackCapabilities): boolean => {
    const fm = (caps as unknown as { focusMode?: string[] }).focusMode || []
    const hasContinuous = Array.isArray(fm) && fm.includes('continuous')
    const hasTorch = !!(caps as unknown as { torch?: boolean }).torch
    return hasContinuous && hasTorch
}

const isFrontFacingTrack = (track: MediaStreamTrack): boolean => {
    try {
        const settings = track.getSettings()
        const facing = settings.facingMode
        if (typeof facing === 'string') {
            const f = facing.toLowerCase()
            if (f === 'user' || f === 'front') return true
        }
    } catch {}
    try {
        const caps = track.getCapabilities?.() as MediaTrackCapabilities & {
            facingMode?: string | string[]
        }
        const fm = caps?.facingMode
        if (Array.isArray(fm)) {
            return fm.some((v) => {
                const f = String(v).toLowerCase()
                return f === 'user' || f === 'front'
            })
        }
        if (typeof fm === 'string') {
            const f = String(fm).toLowerCase()
            if (f === 'user' || f === 'front') return true
        }
    } catch {}
    return false
}

const buildConstraints = (deviceId?: string | null): MediaStreamConstraints[] => {
    const levels = getConstraintsLevels(isMobile.value, deviceId || undefined)
    return levels.map((level) => ({ video: level.video, audio: false }))
}

const enumerateDevicesSafe = async (revealLabels = false): Promise<MediaDeviceInfo[]> => {
    return getCameraDevices({ revealLabels })
}

const pickBestBackByLabel = (
    devices: MediaDeviceInfo[],
    excludeDeviceId?: string
): MediaDeviceInfo | null => {
    const candidates = devices.filter(
        (d) => d.kind === 'videoinput' && (!excludeDeviceId || d.deviceId !== excludeDeviceId)
    )
    if (candidates.length === 0) return null
    const bestId = selectBestCameraByScore(candidates)
    return candidates.find((d) => d.deviceId === bestId) || candidates[0] || null
}

watch(stream, () => attachStreamToVideo())
watch(scanShape, () => updateShades())

const ensureLiveStreamOrCleanup = async (): Promise<boolean> => {
    if (stream.value) {
        const track = stream.value.getVideoTracks()[0]
        if (stream.value.active && track && track.readyState === 'live') {
            attachStreamToVideo()
            try {
                updateSelectedCamera(track)
                updateDeviceInfo(isMobile.value, isSecure.value)
                await optimizeVideoTrack(track, (err) => addError(err))
            } catch {}
            return true
        }
        try {
            stopCamera()
        } catch {}
    }
    return false
}

const guardEnvironment = async (): Promise<boolean> => {
    if (!isSecure.value) {
        error.value = 'The camera is only available via HTTPS or localhost'
        return false
    }
    return true
}

const openWithRetry = async (
    constraintsArray: MediaStreamConstraints[],
    signal?: AbortSignal
): Promise<MediaStreamTrack> => {
    await startCameraWithRetry(constraintsArray, signal)
    const active = stream.value?.getVideoTracks()[0]
    if (!active) throw new Error('Failed to activate the selected camera')
    return active
}

const getActiveTrackOrThrow = (): MediaStreamTrack => {
    const tr = stream.value?.getVideoTracks()[0]
    if (!tr) throw new Error('The active camera is unavailable')
    return tr
}

const finalizeInitialization = async (track: MediaStreamTrack) => {
    updateSelectedCamera(track)
    updateDeviceInfo(isMobile.value, isSecure.value)
    await optimizeVideoTrack(track, (err) => addError(err))
    attachStreamToVideo()
}

const finalizeWithGuard = async (track: MediaStreamTrack) => {
    if (isFrontFacingTrack(track)) {
        const devicesWithLabels = await enumerateDevicesSafe(true)
        const nonFrontDevices = devicesWithLabels.filter((d) => {
            if (d.kind !== 'videoinput') return false
            const l = (d.label || '').toLowerCase()
            return !l.includes('front') && !l.includes('user') && !l.includes('selfie')
        })
        if (nonFrontDevices.length > 0) {
            const bestId = selectBestCameraByScore(nonFrontDevices)
            if (bestId) {
                try {
                    const retryTrack = await openWithRetry(
                        buildConstraints(bestId),
                        abortController.value?.signal
                    )
                    await finalizeInitialization(retryTrack)
                    return
                } catch {}
            }
        }
    }
    await finalizeInitialization(track)
}

const initCamera = async () => {
    isInitializing.value = true
    abortController.value = new AbortController()

    try {
        if (await ensureLiveStreamOrCleanup()) {
            isInitializing.value = false
            return
        }
        if (!(await guardEnvironment())) {
            isInitializing.value = false
            return
        }

        const preferredLabel = getStoredPreferredCameraLabel()
        const devicesWithLabels = await enumerateDevicesSafe(!!preferredLabel)
        updateCamerasList(devicesWithLabels as unknown as CameraScore[])

        let preferredDevice: MediaDeviceInfo | null = null
        if (preferredLabel) {
            preferredDevice =
                devicesWithLabels.find(
                    (d) => d.kind === 'videoinput' && d.label === preferredLabel
                ) || null
        }

        const firstDevice = preferredDevice || pickBestBackByLabel(devicesWithLabels)
        const firstTrack = await openWithRetry(
            buildConstraints(firstDevice?.deviceId),
            abortController.value.signal
        )

        const firstCaps = firstTrack.getCapabilities()
        const firstGood = isGoodCameraByCaps(firstCaps)

        if (firstGood) {
            setStoredPreferredCameraLabel(firstTrack.label)
            await finalizeWithGuard(getActiveTrackOrThrow())
        } else {
            const currentId = firstTrack.getSettings().deviceId || ''
            const best = pickBestBackByLabel(devicesWithLabels, currentId)
            if (best && best.deviceId !== currentId) {
                const retryTrack = await openWithRetry(
                    buildConstraints(best.deviceId),
                    abortController.value.signal
                )
                const retryCaps = retryTrack.getCapabilities()
                if (isGoodCameraByCaps(retryCaps)) setStoredPreferredCameraLabel(retryTrack.label)
            }
            await finalizeWithGuard(getActiveTrackOrThrow())
        }
    } catch (err: unknown) {
        if (err instanceof Error) {
            if (err.name === 'AbortError') return
            if (!error.value) error.value = err.message || 'Failed to start the camera'
            addError(error.value)
        }
    } finally {
        isInitializing.value = false
    }
}

const handleShutter = async () => {
    if (!videoEl.value || !reticleEl.value || !isActive.value) return
    try {
        const { file, url } = await captureAndCrop(videoEl.value, reticleEl.value)
        previewImageUrl.value = url
        isLoadingMock.value = true
        await new Promise((resolve) => setTimeout(resolve, 2000))
        await sendImageForRegistration(file)
    } catch (e) {
        console.error('Capture failed:', e)
        mockError.value = 'Failed to capture image'
        clearPreviewImage()
        isLoadingMock.value = false
    }
}

const sendImageForRegistration = async (imageFile: File) => {
    try {
        mockError.value = null
        mockErrorKind.value = 'scan'
        attachedProductId.value = null
        attachedProductName.value = null

        const registerStore = useRegisterStore()
        registerStore.reset()
        const result = await registerStore.registerLabel(imageFile, scanShape.value)

        if (result.success && result.data) {
            const labelInfo = await apiService.getLabelInfo(result.data.uniq_label_id)
            if (!labelInfo.success || !labelInfo.data) {
                mockError.value = labelInfo.error?.message || 'Failed to check label'
                clearPreviewImage()
                isLoadingMock.value = false
                return
            }

            if (labelInfo.data.product_id) {
                mockErrorKind.value = 'already-attached'
                attachedProductId.value = labelInfo.data.product_id
                attachedProductName.value = labelInfo.data.product_name
                mockError.value = 'Label already attached'
                registerStore.reset()
                clearPreviewImage()
                isLoadingMock.value = false
                return
            }

            await turnOffFlash()
            clearPreviewImage()
            isLoadingMock.value = false
            await router.push({ name: 'register-select' })
        } else {
            mockError.value = result.error?.message || 'Label recognition failed'
            clearPreviewImage()
            isLoadingMock.value = false
        }
    } catch (err) {
        console.error('Registration recognition error:', err)
        mockError.value = 'Label recognition error'
        clearPreviewImage()
        isLoadingMock.value = false
    }
}

const clearPreviewImage = () => {
    revokeObjectURL(previewImageUrl.value)
    previewImageUrl.value = null
}

const closeError = () => {
    if (isErrorModalClosing.value) return
    isErrorModalClosing.value = true
    isDragging.value = false
    errorDragY.value = 0
    setTimeout(() => {
        mockError.value = null
        mockErrorKind.value = 'scan'
        attachedProductId.value = null
        attachedProductName.value = null
        isErrorModalClosing.value = false
        clearPreviewImage()
    }, 200)
}

let swipeStartY = 0
let swipeStartTime = 0
const SWIPE_THRESHOLD = 100
const SWIPE_TIME_THRESHOLD = 1000

const handleSwipeStart = (e: TouchEvent) => {
    if (e.touches.length === 1) {
        swipeStartY = e.touches[0].clientY
        swipeStartTime = Date.now()
        isDragging.value = true
        errorDragY.value = 0
    }
}

const handleSwipeMove = (e: TouchEvent) => {
    if (!swipeStartY || e.touches.length === 0 || !isDragging.value) return
    const currentY = e.touches[0].clientY
    const deltaY = currentY - swipeStartY
    if (deltaY > 0) {
        errorDragY.value = deltaY
        e.preventDefault()
        e.stopPropagation()
    } else {
        errorDragY.value = 0
    }
}

const handleSwipeEnd = (e: TouchEvent) => {
    if (!isDragging.value) return
    const swipeEndY = e.changedTouches[0]?.clientY || swipeStartY
    const swipeDistance = swipeEndY - swipeStartY
    const swipeTime = Date.now() - swipeStartTime
    isDragging.value = false
    if (swipeDistance > SWIPE_THRESHOLD && swipeTime < SWIPE_TIME_THRESHOLD) {
        const screenHeight = window.innerHeight
        errorDragY.value = screenHeight + 100
        isErrorModalClosing.value = true
        setTimeout(() => {
            mockError.value = null
            isErrorModalClosing.value = false
            errorDragY.value = 0
            clearPreviewImage()
        }, 300)
    } else {
        errorDragY.value = 0
    }
    swipeStartY = 0
    swipeStartTime = 0
}

const handleSwipeCancel = () => {
    if (isDragging.value) {
        isDragging.value = false
        errorDragY.value = 0
        swipeStartY = 0
        swipeStartTime = 0
    }
}

// const setZoomLevel = async (level: number) => {
//     if (!stream.value) return
//     const videoTrack = stream.value.getVideoTracks()[0]
//     if (!videoTrack) return
//     const success = await setZoom(videoTrack, level)
//     if (success) zoomLevel.value = level
// }

const handleToggleFlash = async () => {
    if (!stream.value) return
    const videoTrack = stream.value.getVideoTracks()[0]
    if (!videoTrack) return
    const newState = !isFlashOn.value
    const success = await toggleFlash(videoTrack, newState)
    if (success) isFlashOn.value = newState
}

const turnOffFlash = async () => {
    if (!stream.value || !isFlashOn.value) return
    const videoTrack = stream.value.getVideoTracks()[0]
    if (!videoTrack) return
    const success = await toggleFlash(videoTrack, false)
    if (success) isFlashOn.value = false
}

const updateShades = () => {
    const o = overlayEl.value?.getBoundingClientRect()
    overlayW.value = Math.round(o!.width)
    overlayH.value = Math.round(o!.height)
    hole.value = calculateScanHole(overlayW.value, overlayH.value, scanShape.value)
}

let updateShadesTimeout: ReturnType<typeof setTimeout> | null = null
const debouncedUpdateShades = () => {
    if (updateShadesTimeout) clearTimeout(updateShadesTimeout)
    updateShadesTimeout = setTimeout(updateShades, 16)
}

let ro: ResizeObserver | null = null

watch(
    () => errorContentEl.value,
    (el, prevEl) => {
        if (prevEl) {
            prevEl.removeEventListener('touchstart', handleSwipeStart)
            prevEl.removeEventListener('touchmove', handleSwipeMove)
            prevEl.removeEventListener('touchend', handleSwipeEnd)
            prevEl.removeEventListener('touchcancel', handleSwipeCancel)
        }
        if (el && !isErrorModalClosing.value) {
            el.addEventListener('touchstart', handleSwipeStart, { passive: false })
            el.addEventListener('touchmove', handleSwipeMove, { passive: false })
            el.addEventListener('touchend', handleSwipeEnd, { passive: true })
            el.addEventListener('touchcancel', handleSwipeCancel, { passive: true })
        }
    }
)

onMounted(() => {
    initCamera()
    updateShades()
    ro = new ResizeObserver(debouncedUpdateShades)
    if (overlayEl.value) ro.observe(overlayEl.value)
    window.addEventListener('resize', debouncedUpdateShades)
})

onUnmounted(async () => {
    await turnOffFlash()
    if (abortController.value) abortController.value.abort()
    if (updateShadesTimeout) clearTimeout(updateShadesTimeout)
    if (errorContentEl.value) {
        errorContentEl.value.removeEventListener('touchstart', handleSwipeStart)
        errorContentEl.value.removeEventListener('touchmove', handleSwipeMove)
        errorContentEl.value.removeEventListener('touchend', handleSwipeEnd)
        errorContentEl.value.removeEventListener('touchcancel', handleSwipeCancel)
    }
    clearPreviewImage()
    ro?.disconnect()
    window.removeEventListener('resize', debouncedUpdateShades)
})
</script>

<style scoped lang="stylus">
.scan
    display flex
    flex-direction column
    gap 16px

    &__camera
        position relative

    &__video-wrap
        position relative
        width 100%
        height calc(var(--vh, 1vh) * 100 - var(--app-footer-height))
        border-radius 0
        overflow hidden
        background var(--app-black)

    &__video
        display block
        width 100%
        height 100%
        object-fit cover
        object-position center

    &__shutter-wrap
        position absolute
        left 0
        right 0
        bottom 15px
        display flex
        justify-content center
        pointer-events none

    &__zoom-controls
        width 80px
        height 30px
        position absolute
        left 50%
        bottom 103px
        transform translateX(-50%)
        display flex
        gap 5px
        justify-content center
        pointer-events none
        align-items center
        background rgba(217, 217, 217, 0.15)
        border-radius 50px

    &__top-bar
        position absolute
        top 0
        left 0
        right 0
        z-index 20
        display flex
        align-items center
        justify-content space-between
        padding 12px 16px
        background rgba(0, 0, 0, 0.45)
        pointer-events all

    &__shape-switcher
        display flex
        align-items center
        gap 4px
        background rgba(255, 255, 255, 0.12)
        border-radius 10px
        padding 4px

    &__top-right
        display flex
        align-items center
        min-width 32px
        justify-content flex-end

    &__top-placeholder
        width 32px
        height 32px

    &__top-btn
        flex-shrink 0

    &__error
        color var(--app-error)
        font-size 14px
        &-modal
            position fixed
            inset 0
            background rgba(0,0,0,.5)
            display flex
            align-items center
            flex-direction column
            justify-content flex-end
            z-index 90
            margin-bottom var(--app-footer-height)
            overscroll-behavior contain
            touch-action pan-y
            &-bg
                position absolute
                left 0
                top 0
                width 100%
                height 100%
                touch-action none

    &__help
        &-modal
            position fixed
            left 0
            top 0
            display flex
            align-items center
            justify-content center
            padding 28px 16px 16px
            z-index 90
            &-bg
                position absolute
                left 0
                top 0
                width 100%
                height 100%
                background rgba(0,0,0,0.8)
        &-content
            position relative
            z-index 10
            width 100%
            color var(--app-text-light-color)
        &-close
            position absolute
            top -5px
            right 0
            cursor pointer
        &-title
            margin-bottom 11px
            font-size 20px
            line-height 24px
            font-weight 600
        &-desc
            font-size 16px
            line-height 24px
            font-weight 400

    &__loader-modal
        position fixed
        inset 0
        background rgba(0,0,0,.5)
        display flex
        align-items center
        justify-content center
        z-index 80

    &__initializing
        position absolute
        inset 0
        background rgba(0,0,0,.7)
        display flex
        align-items center
        justify-content center
        z-index 80

.help
    width 32px
    height 32px
    display inline-flex
    align-items center
    justify-content center
    cursor pointer
    background transparent
    border none
    padding 0

.shutter
    pointer-events all
    width 68px
    height 68px
    border-radius 50%
    background transparent
    border 4px solid var(--app-bg-color)
    display inline-flex
    align-items center
    justify-content center
    transition transform .1s ease
    &:active
        transform scale(.98)
    &:disabled
        opacity .6
        cursor not-allowed
    &__inner
        display block
        width 52px
        height 52px
        border-radius 50%
        background var(--app-bg-color)

.zoom-btn
    pointer-events all
    width 16px
    height 16px
    border-radius 50%
    background rgba(0, 0, 0, 0.35)
    border 0
    color var(--app-text-light-color)
    font-size 8px
    line-height 8px
    font-weight 400
    display flex
    align-items center
    justify-content center
    cursor pointer
    transition all 0.2s ease
    font-family 'SF Pro', sans-serif
    &_active
        color var(--app-warning)
        font-size 12px
        line-height 12px
        width 24px
        height 24px
        &:after
            content 'x'
            font inherit
            color inherit
            display inline

.flash-btn
    pointer-events all
    width 32px
    height 32px
    cursor pointer
    transition all 0.2s ease

.shape-btn
    display inline-flex
    align-items center
    justify-content center
    width 40px
    height 32px
    border-radius 7px
    border none
    background transparent
    color rgba(255, 255, 255, 0.55)
    cursor pointer
    transition background 0.15s ease, color 0.15s ease
    padding 0
    &_active
        background rgba(255, 255, 255, 0.25)
        color rgba(255, 255, 255, 1)

.loader
    display flex
    flex-direction column
    align-items center
    text-align center
    &__spinner
        width 45px
        height auto
        animation spin 1.5s ease-in-out infinite
    &__title
        margin-top 20px
        color var(--app-text-light-color)
        font-size 16px
        line-height 20px
        font-weight 600

@keyframes spin
    0%
        transform rotate(0deg)
    100%
        transform rotate(360deg)

.error-modal-enter-active,
.error-modal-leave-active
    transition opacity 0.2s ease-out, transform 0.2s ease-out

.error-modal-enter-from
    opacity 0
    transform translateY(100%)

.error-modal-leave-to
    opacity 0
    transform translateY(100%)

.error-modal-enter-active .error,
.error-modal-leave-active .error
    transition transform 0.2s ease-out, opacity 0.2s ease-out

.error-modal-enter-from .error,
.error-modal-leave-to .error
    transform translateY(100%)
    opacity 0

.error
    position relative
    width 100%
    background var(--app-bg-color)
    color var(--app-text-color)
    border-radius 16px 16px 0 0
    padding 37px 16px 24px
    text-align center
    display flex
    flex-direction column
    align-items center
    touch-action pan-y
    &__swipe
        position absolute
        top 8px
        width 40px
        height 5px
        background var(--app-text-grey-color)
        border-radius 10px
        z-index 10
    &__text-wrap
        display flex
        flex-direction column
        align-items center
        gap 16px
    &__img
        width 70%
        height auto
        margin 24px auto 32px
    &__title
        font-size 24px
        line-height 30px
        font-weight 600
    &__desc
        max-width 358px
        font-size 16px
        line-height 24px
    &__actions
        display flex
        justify-content center
        width 100%
</style>
